import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Badge } from '../components/ui/badge';
import { 
  Trophy, Play, ChevronRight, ChevronLeft, Share2, Copy, CheckCircle,
  ExternalLink, Twitter, Star, Ruler, MapPin, GraduationCap, Mail, Users
} from 'lucide-react';
import { API } from '../lib/constants';

const DemoCard = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [offers, setOffers] = useState([]);
  const [highlights, setHighlights] = useState([]);
  const [stats, setStats] = useState([]);
  const [currentCard, setCurrentCard] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/api/profile/dcook1`);
        setProfile(res.data);
        setHighlights(res.data.highlights || []);
        const userId = res.data.profile?.user_id;
        if (userId) {
          const [offersRes, statsRes] = await Promise.all([
            axios.get(`${API}/api/dashboard/${userId}/offers`).catch(() => ({ data: { offers: [] } })),
            axios.get(`${API}/api/dashboard/${userId}/comprehensive-stats`).catch(() => ({ data: { stats: [] } })),
          ]);
          setOffers(offersRes.data.offers || []);
          setStats(statsRes.data.stats || []);
        }
      } catch (e) { console.error(e); }
    };
    fetch();
  }, []);

  const cards = ['hero', 'offers', 'highlights', 'stats', 'about'];
  const totalCards = cards.length;
  
  const nextCard = () => setCurrentCard(prev => Math.min(prev + 1, totalCards - 1));
  const prevCard = () => setCurrentCard(prev => Math.max(prev - 1, 0));

  const handleTouchStart = (e) => setTouchStart(e.touches[0].clientX);
  const handleTouchEnd = (e) => {
    if (!touchStart) return;
    const diff = touchStart - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) {
      if (diff > 0) nextCard();
      else prevCard();
    }
    setTouchStart(null);
  };

  if (!profile) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-[#16A34A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const p = profile.profile;
  const measurables = profile.measurables || {};

  return (
    <div className="min-h-screen bg-black text-white overflow-hidden select-none">
      {/* Demo Banner */}
      <div className="bg-[#0F5132] text-white text-center py-2 px-4 text-xs font-bold z-50 relative">
        ⚡ DEMO: Trading Card Layout — <button onClick={() => navigate('/')} className="underline">Back to Home</button> 
        {' '}| <button onClick={() => navigate('/demo/bento')} className="underline">View Bento Layout →</button>
      </div>

      {/* Card Container */}
      <div className="h-[calc(100vh-32px)] relative max-w-5xl mx-auto" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
        
        {/* Card Dots Indicator */}
        <div className="absolute top-4 left-0 right-0 z-40 flex justify-center gap-1.5">
          {cards.map((_, i) => (
            <button key={i} onClick={() => setCurrentCard(i)}
              className={`h-1 rounded-full transition-all duration-300 ${
                i === currentCard ? 'w-6 bg-[#16A34A]' : 'w-1.5 bg-white/20'
              }`} />
          ))}
        </div>

        {/* Navigation Arrows */}
        {currentCard > 0 && (
          <button onClick={prevCard} className="absolute left-2 top-1/2 -translate-y-1/2 z-40 w-10 h-10 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center">
            <ChevronLeft className="h-5 w-5" />
          </button>
        )}
        {currentCard < totalCards - 1 && (
          <button onClick={nextCard} className="absolute right-2 top-1/2 -translate-y-1/2 z-40 w-10 h-10 bg-[#16A34A]/20 backdrop-blur-sm rounded-full flex items-center justify-center text-[#16A34A]">
            <ChevronRight className="h-5 w-5" />
          </button>
        )}

        {/* Cards */}
        <div className="h-full flex transition-transform duration-500 ease-out" 
          style={{ transform: `translateX(-${currentCard * 100}%)` }}>
          
          {/* ═══ CARD 1: HERO ═══ */}
          <div className="w-full h-full flex-shrink-0 relative lg:flex lg:items-stretch">
            {/* Full bleed photo */}
            <div className="absolute inset-0 lg:relative lg:w-1/2 lg:flex-shrink-0">
              {p.profile_picture_url && (
                <img src={p.profile_picture_url} alt="" className="w-full h-full object-cover" />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent lg:bg-gradient-to-r lg:from-transparent lg:via-transparent lg:to-black" />
            </div>
            
            {/* Content */}
            <div className="absolute bottom-0 left-0 right-0 p-8 pb-12 lg:relative lg:w-1/2 lg:flex lg:flex-col lg:justify-center lg:p-12 lg:bg-black">
              <div className="flex gap-2 mb-3">
                {p.positions?.map((pos, i) => (
                  <span key={i} className="bg-[#0F5132] text-white text-sm lg:text-base font-black px-3 lg:px-4 py-1 lg:py-1.5 rounded-full">{pos}</span>
                ))}
              </div>
              <h1 className="text-5xl sm:text-7xl lg:text-8xl font-black leading-[0.9] mb-2 tracking-tight">{p.name}</h1>
              <p className="text-white/50 text-lg lg:text-xl mb-1">{p.school}</p>
              <div className="flex items-center gap-3 text-white/40 text-sm lg:text-base">
                <span>Class of {p.class_year}</span>
                {p.city && <><span>•</span><span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{p.city}, {p.state}</span></>}
              </div>
              
              {/* Quick stats */}
              <div className="flex gap-6 lg:gap-10 mt-6 lg:mt-10">
                {p.height && (
                  <div>
                    <p className="text-3xl lg:text-5xl font-black text-white">{p.height}</p>
                    <p className="text-white/30 text-xs lg:text-sm uppercase tracking-wider">Height</p>
                  </div>
                )}
                {p.weight && (
                  <div>
                    <p className="text-3xl lg:text-5xl font-black text-white">{p.weight}</p>
                    <p className="text-white/30 text-xs lg:text-sm uppercase tracking-wider">Weight</p>
                  </div>
                )}
                {p.gpa && (
                  <div className="hidden lg:block">
                    <p className="text-5xl font-black text-white">{p.gpa}</p>
                    <p className="text-white/30 text-sm uppercase tracking-wider">GPA</p>
                  </div>
                )}
              </div>
              
              <p className="text-[#16A34A]/60 text-xs lg:text-sm mt-6 lg:mt-10">Swipe → for offers, highlights & stats</p>
            </div>
          </div>

          {/* ═══ CARD 2: OFFERS ═══ */}
          <div className="w-full h-full flex-shrink-0 bg-gradient-to-b from-zinc-950 to-black flex flex-col justify-center p-8 lg:p-16">
            <div className="flex items-center gap-2 mb-2">
              <Trophy className="h-6 w-6 text-[#16A34A]" />
              <h2 className="text-3xl lg:text-5xl font-black">Offers</h2>
            </div>
            <p className="text-white/30 text-sm mb-8">{p.name}'s recruiting status</p>
            
            {offers.length === 0 ? (
              <div className="text-center py-12">
                <Trophy className="h-16 w-16 text-white/5 mx-auto mb-4" />
                <p className="text-white/20 text-lg">No offers yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {offers.map((offer, i) => (
                  <div key={i} className="bg-white/[0.04] border border-white/10 rounded-2xl p-5 flex items-center gap-4">
                    {offer.college?.logoUrl ? (
                      <img src={offer.college.logoUrl} alt="" className="w-14 h-14 rounded-xl object-contain bg-white p-2" />
                    ) : (
                      <div className="w-14 h-14 rounded-xl bg-white/10 flex items-center justify-center text-white/20 font-bold">?</div>
                    )}
                    <div className="flex-1">
                      <p className="font-black text-lg">{offer.college?.name || 'University'}</p>
                      <p className="text-white/30 text-sm">{offer.college?.division}</p>
                    </div>
                    <Badge className={`text-sm font-bold border-0 px-3 py-1 ${
                      offer.status === 'committed' ? 'bg-green-500 text-white' :
                      offer.status === 'offer' ? 'bg-[#0F5132] text-white' : 'bg-white/10 text-white/50'
                    }`}>{offer.status}</Badge>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ═══ CARD 3: HIGHLIGHTS ═══ */}
          <div className="w-full h-full flex-shrink-0 bg-gradient-to-b from-zinc-950 to-black flex flex-col justify-center p-8 lg:p-16">
            <div className="flex items-center gap-2 mb-2">
              <Play className="h-6 w-6 text-[#16A34A]" />
              <h2 className="text-3xl lg:text-5xl font-black">Highlights</h2>
            </div>
            <p className="text-white/30 text-sm mb-8">{highlights.length} highlight reel{highlights.length !== 1 ? 's' : ''}</p>
            
            <div className="space-y-3 overflow-y-auto max-h-[60vh]">
              {highlights.map((vid, i) => (
                <a key={i} href={vid.hudl_video_url} target="_blank" rel="noopener noreferrer"
                  className="block bg-white/[0.04] border border-white/10 rounded-2xl overflow-hidden hover:border-[#16A34A]/30 transition-all">
                  <div className="aspect-video relative">
                    {vid.og_image ? (
                      <img src={vid.og_image} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-white/5 flex items-center justify-center">
                        <Play className="h-10 w-10 text-white/10" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                      <div className="w-14 h-14 bg-[#16A34A] rounded-full flex items-center justify-center">
                        <Play className="h-6 w-6 text-black ml-1" />
                      </div>
                    </div>
                  </div>
                  <div className="p-4">
                    <p className="font-bold text-lg">{vid.video_title || `Highlight ${i + 1}`}</p>
                  </div>
                </a>
              ))}
            </div>
          </div>

          {/* ═══ CARD 4: STATS ═══ */}
          <div className="w-full h-full flex-shrink-0 bg-gradient-to-b from-zinc-950 to-black flex flex-col justify-center p-8 lg:p-16">
            <div className="flex items-center gap-2 mb-2">
              <Star className="h-6 w-6 text-[#16A34A]" />
              <h2 className="text-3xl lg:text-5xl font-black">Measurables</h2>
            </div>
            <p className="text-white/30 text-sm mb-8">Athletic testing & measurements</p>
            
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
              {[
                { label: 'HEIGHT', value: p.height },
                { label: 'WEIGHT', value: p.weight ? `${p.weight} lbs` : null },
                { label: '40-YARD', value: measurables?.forty_yard_dash ? `${measurables.forty_yard_dash}s` : null },
                { label: 'BENCH', value: measurables?.bench_press ? `${measurables.bench_press} lbs` : null },
                { label: 'SQUAT', value: measurables?.squat ? `${measurables.squat} lbs` : null },
                { label: 'VERTICAL', value: measurables?.vertical_jump ? `${measurables.vertical_jump}"` : null },
              ].filter(s => s.value).map((stat, i) => (
                <div key={i} className="bg-white/[0.04] border border-white/10 rounded-2xl p-5 text-center">
                  <p className="text-3xl font-black text-white mb-1">{stat.value}</p>
                  <p className="text-white/30 text-[10px] uppercase tracking-[0.2em]">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* ═══ CARD 5: ABOUT / CTA ═══ */}
          <div className="w-full h-full flex-shrink-0 bg-gradient-to-b from-zinc-950 to-black flex flex-col justify-center items-center p-8 text-center">
            <div className="w-20 h-20 rounded-full overflow-hidden ring-4 ring-[#16A34A]/30 ring-offset-4 ring-offset-black mb-6">
              {p.profile_picture_url ? (
                <img src={p.profile_picture_url} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-[#16A34A] flex items-center justify-center text-2xl font-black text-black">{p.name?.charAt(0)}</div>
              )}
            </div>
            <h2 className="text-2xl font-black mb-1">{p.name}</h2>
            <p className="text-white/40 text-sm mb-6">{p.school} • {p.positions?.join(', ')}</p>
            
            {p.tagline && (
              <p className="text-[#16A34A]/60 italic text-sm mb-6">"{p.tagline}"</p>
            )}
            
            <div className="flex gap-3 mb-8">
              {p.hudl_url && (
                <a href={p.hudl_url} target="_blank" rel="noopener noreferrer"
                  className="bg-white/10 px-4 py-2 rounded-full text-xs font-bold flex items-center gap-1.5 hover:bg-white/20">
                  <ExternalLink className="h-3 w-3" /> HUDL
                </a>
              )}
              <button onClick={() => { navigator.clipboard.writeText(window.location.href); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
                className="bg-white/10 px-4 py-2 rounded-full text-xs font-bold flex items-center gap-1.5 hover:bg-white/20">
                {copied ? <CheckCircle className="h-3 w-3 text-[#16A34A]" /> : <Copy className="h-3 w-3" />}
                {copied ? 'Copied!' : 'Copy Link'}
              </button>
            </div>

            {/* CTA */}
            <div className="bg-[#16A34A]/10 border border-[#16A34A]/20 rounded-2xl p-6 w-full">
              <div className="w-10 h-10 bg-[#1a1a1a] rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-[#16A34A] font-black">O</span>
              </div>
              <p className="font-black text-lg mb-1">Get your own Offer profile</p>
              <p className="text-white/30 text-xs mb-4">Create your link-in-bio in 60 seconds</p>
              <button onClick={() => navigate('/create-profile')}
                className="bg-[#0F5132] text-white font-bold px-8 py-3 rounded-full text-sm hover:bg-[#0B3B24] transition-all">
                Create Profile
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DemoCard;
